import { Container } from '@/components/layout';
import { CurveDivider, Sparkle } from '@/components/ornaments';
import { ParallaxLayer, Reveal } from '@/components/motion';
import { CtaButton } from '@/components/ui';
import { Eyebrow, Lead } from '@/components/typography';
import { AssetImage } from '@/pages/shared/AssetImage';
import { HERO } from '../hub.content';
import styles from './HubHero.module.css';

/**
 * Hero del hub. Composicion del mockup: titular de 3 lineas a la izquierda con
 * la tercera en oro, retrato de Azrahel a la derecha, galaxia luminosa detras y
 * separador curvo dorado que enlaza con el mantra.
 *
 * Los dos CTA son anclas INTERNAS del propio hub (no son CTA de compra): por eso
 * no les aplica el estado `coming-soon` del ADR-003 §3, y por eso apuntan a un
 * `id` que existe de verdad en esta pagina — nunca a `#` a secas.
 */
export function HubHero() {
  return (
    <section className={styles.hero} aria-labelledby="hub-hero-title">
      <div className={styles.sky} aria-hidden="true">
        <ParallaxLayer distance={28}>
          {/* Galaxia + bahia al amanecer: UNA sola banda en el catalogo de
              FORGE-BRAVO, no dos capas. `eager` porque esta sobre el pliegue. */}
          <AssetImage
            asset="hub/hero-cielo-galaxia"
            className={styles.skyMedia}
            sizes="100vw"
            loading="eager"
            alt=""
          />
        </ParallaxLayer>
      </div>

      <Container width="wide">
        <div className={styles.grid}>
          <div className={styles.copy}>
            <Reveal>
              {/* `.az-on-photo`: la ceja son 16 px de oro apoyados en la
                  fotografia del cielo, y ahi NO hay ratio fijo posible — el
                  fondo cambia pixel a pixel. Medido sobre el DOM renderizado
                  daba 3,42:1 (AA exige 4,5). El token de ALPHA hace el fondo
                  determinista bajo los glifos. PAGE_CONTRACT §3.3.bis. */}
              <Eyebrow className={styles.eyebrowOnSky} ink="gold" withSparkles>
                {HERO.eyebrow}
              </Eyebrow>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className={styles.title} id="hub-hero-title">
                <span className={styles.line}>{HERO.lines[0]}</span>
                <span className={styles.line}>{HERO.lines[1]}</span>
                <span className={`${styles.line} ${styles.lineGold}`}>{HERO.lines[2]}</span>
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <Lead italic className={styles.subtitle}>
                {HERO.subtitle}
              </Lead>
            </Reveal>

            <Reveal delay={0.24}>
              <div className={styles.actions}>
                <CtaButton as="internal" to="#destinos" variant="primary" icon={<Sparkle size={18} />}>
                  {HERO.ctaPrimary}
                </CtaButton>
                <CtaButton
                  as="internal"
                  to="#experiencia"
                  variant="outline"
                  icon={<Sparkle size={16} variant="line" />}
                >
                  {HERO.ctaSecondary}
                </CtaButton>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12} className={styles.portrait}>
            <AssetImage
              asset="azrahel/recorte"
              className={styles.portraitMedia}
              sizes="(min-width: 768px) 42vw, 100vw"
              loading="eager"
              alt={HERO.portraitAlt}
            />
          </Reveal>
        </div>
      </Container>

      <CurveDivider className={styles.curve} direction="down" fill="alt" withGoldEdge />
    </section>
  );
}
